

import { 
  Modal, 
  Form, 
  Title, 
  Text, 
  Button
} from '@bancobarigui/barihaus';
import { Input } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';
import { Divider } from 'antd';

import { InspectionRequest } from '@/services/types/inspection.types';
import { useProviderResponseModal } from './useProviderResponseModal';

interface ProviderResponseModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  assignmentId: string;
  inspectionData?: InspectionRequest | undefined;
  actionType: 'accept' | 'reject';
}

export function ProviderResponseModal({
  open,
  onClose,
  onSuccess,
  assignmentId,
  inspectionData,
  actionType,
}: ProviderResponseModalProps) {
  const {
    form,
    isSubmitting,
    handleFormSubmit,
  } = useProviderResponseModal({
    assignmentId,
    onClose,
    onSuccess,
    initialActionType: actionType,
  });

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          {actionType === 'accept' ? (
            <>
              <CheckCircleOutlined className="text-green-500" />
              <span>Aceitar Inspeção</span>
            </>
          ) : (
            <>
              <CloseCircleOutlined className="text-red-500" />
              <span>Recusar Inspeção</span>
            </>
          )}
        </div>
      }
      open={open}
      onCancel={handleClose}
      width={600}
      footer={null}
      destroyOnClose
    >
      <div className="space-y-4">
        {inspectionData && (
          <>
            <div className="bg-blue-50 p-4 rounded-lg">
              <Title level={5} className="!mb-2">Detalhes da Inspeção</Title>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Text className="text-gray-600">Número da Proposta:</Text>
                  <Text strong>{inspectionData.proposalNumber}</Text>
                </div>
                <div className="flex justify-between">
                  <Text className="text-gray-600">Proponente:</Text>
                  <Text>{inspectionData.proponents?.map(p => p.name).join(', ') || 'N/A'}</Text>
                </div>
                <div className="flex justify-between">
                  <Text className="text-gray-600">Endereço:</Text>
                  <Text>{inspectionData.items?.[0]?.street}, {inspectionData.items?.[0]?.number}</Text>
                </div>
                <div className="flex justify-between">
                  <Text className="text-gray-600">Cidade:</Text>
                  <Text>{inspectionData.items?.[0]?.city}/{inspectionData.items?.[0]?.state}</Text>
                </div>
              </div>
            </div>
            <Divider className="!my-4" />
          </>
        )}

        <div className={`p-4 rounded-lg ${actionType === 'accept' ? 'bg-green-50' : 'bg-red-50'}`}>
          <Title level={5} className={`!mb-2 ${actionType === 'accept' ? 'text-green-800' : 'text-red-800'}`}>
            <InfoCircleOutlined className="mr-2" />
            {actionType === 'accept' ? 'Confirmar Aceite' : 'Confirmar Recusa'}
          </Title>
          <Text className={actionType === 'accept' ? 'text-green-700' : 'text-red-700'}>
            {actionType === 'accept' 
              ? 'Confirme o aceite da inspeção. Após aceitar, você poderá direcioná-la para um inspetor.'
              : 'Confirme a recusa da inspeção. Informe o motivo para que o banco possa redirecionar adequadamente.'
            }
          </Text>
        </div>

        <Form 
          form={form} 
          onFinish={handleFormSubmit}
          layout="vertical"
          className="mt-4"
        >
          {actionType === 'accept' ? (
            <Form.Item
              name="notes"
              label="Observações do Aceite (opcional)"
              help="Adicione observações sobre o aceite da inspeção"
            >
              <Input.TextArea
                placeholder="Ex: Prestadora aceita a inspeção e irá direcionar para um inspetor qualificado..."
                rows={3}
                maxLength={500}
                showCount
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="rejectionNotes"
              label="Motivo da Recusa"
              rules={[
                { required: true, message: 'Por favor, informe o motivo da recusa' },
                { min: 10, message: 'O motivo deve ter pelo menos 10 caracteres' }
              ]}
              help="Explique o motivo da recusa para que o banco possa redirecionar adequadamente"
            >
              <Input.TextArea
                placeholder="Ex: Prestadora não tem disponibilidade para realizar esta inspeção no prazo solicitado..."
                rows={4}
                maxLength={500}
                showCount
              />
            </Form.Item>
          )}

          <div className="flex justify-between pt-4">
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>

            <Button
              buttonColor="primary"
              htmlType="submit"
              loading={isSubmitting}
              icon={actionType === 'accept' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            >
              {actionType === 'accept' ? 'Confirmar Aceite' : 'Confirmar Recusa'}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}